import React, { Dispatch, useReducer, useMemo } from 'react';

export const DOUBLE_THE_DONATION_CAMPAIGN = 'ALL_CONTRIBUTIONS';

// Based on https://doublethedonation.com/api/360matchpro/setup/custom_implementation.html
export interface DoubleTheDonationState {
  /** Required: The key used to tie this donation to your Double the Donation account. */
  matchproPublicKey: string | boolean;

  /** Required: Campaign name to group donations. */
  campaign: string | boolean;

  /** Required: An identifier for the donation, can contain spaces and special characters. */
  donationIdentifier: string | false;

  /** Strongly Recommended: The email address associated with the donation. */
  donorEmail?: string;

  /** Strongly Recommended: The first name of the donor. */
  donorFirstName?: string;

  /** Strongly Recommended: The last name of the donor. */
  donorLastName?: string;

  /** Strongly Recommended: The dollar amount of the donation as an integer or decimal. */
  donationAmount?: string;
  donationDatetime?: string;
  donorPhone?: string;
  doubleTheDonationCompanyId?: string;

  /** Do not send */
  privateDoubleTheDonationCompanyName?: string;
}

interface UpdateDoubleTheDonation {
  type: 'updateDoubleTheDonation';
  payload: Partial<DoubleTheDonationState>;
}

export type DoubleTheDonationAction = UpdateDoubleTheDonation;

export const initState = {
  matchproPublicKey: false,
  campaign: false,
  donationIdentifier: false as const,
};

export const reducer = (state: DoubleTheDonationState, action: DoubleTheDonationAction) => {
  let newState = state;

  switch (action.type) {
    case 'updateDoubleTheDonation': {
      newState = { ...state, ...action.payload };
      break;
    }
    default: {
      throw new Error(
        'Provided action type does not exist. Did you mean "updateDoubleTheDonation"?',
      );
    }
  }

  return newState;
};

export const DoubleTheDonationContext = React.createContext<{
  state: DoubleTheDonationState;
  dispatch: Dispatch<DoubleTheDonationAction>;
}>(null);

export const DoubleTheDonationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initState);

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <DoubleTheDonationContext.Provider value={contextValue}>
      {children}
    </DoubleTheDonationContext.Provider>
  );
};
export function useDoubleTheDonation() {
  const context = React.useContext(DoubleTheDonationContext);

  if (context === undefined) {
    throw new Error('useDoubleTheDonation must be used within a DoubleTheDonationProvider');
  }

  return context;
}
